export const PRIVACY_CONSENT_STORAGE_KEY = "rauxa.privacy-consent";
export const PRIVACY_ANALYTICS_SESSION_STORAGE_KEY = "rauxa.analytics.session";
export const PRIVACY_CONSENT_VERSION = "2026-07-17";

export type PrivacyConsentSource = "user" | "gpc";

export type PrivacyConsentPreferences = {
  version: typeof PRIVACY_CONSENT_VERSION;
  necessary: true;
  functional: boolean;
  analytics: boolean;
  advertising: boolean;
  updatedAt: string;
  source: PrivacyConsentSource;
  gpc: boolean;
};

type NavigatorWithGlobalPrivacyControl = Navigator & {
  globalPrivacyControl?: boolean;
};

export const isGlobalPrivacyControlEnabled = (): boolean =>
  typeof navigator !== "undefined" &&
  (navigator as NavigatorWithGlobalPrivacyControl).globalPrivacyControl === true;

const isStoredPreferences = (
  value: unknown
): value is PrivacyConsentPreferences => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<PrivacyConsentPreferences>;
  return (
    candidate.version === PRIVACY_CONSENT_VERSION &&
    candidate.necessary === true &&
    typeof candidate.functional === "boolean" &&
    typeof candidate.analytics === "boolean" &&
    typeof candidate.advertising === "boolean" &&
    typeof candidate.updatedAt === "string" &&
    (candidate.source === "user" || candidate.source === "gpc") &&
    typeof candidate.gpc === "boolean"
  );
};

export const readPrivacyConsent = (): PrivacyConsentPreferences | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(PRIVACY_CONSENT_STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue) as unknown;
    return isStoredPreferences(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

export const writePrivacyConsent = (
  preferences: PrivacyConsentPreferences
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      PRIVACY_CONSENT_STORAGE_KEY,
      JSON.stringify(preferences)
    );
  } catch {
    // Consent remains active for this page view when storage is unavailable.
  }
};

export const clearAnalyticsSessionStorage = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(PRIVACY_ANALYTICS_SESSION_STORAGE_KEY);
  } catch {
    // Optional session storage cleanup is best-effort.
  }
};

export const clearMapboxStorage = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith("mapbox.eventData")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Optional vendor storage cleanup is best-effort.
  }
};
